"use client";

import * as React from "react";
import {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ChevronDown,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  Plus,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUserContext, User } from "@/context/user-context";
import { useRole } from "@/context/role-context";
import { useBranch } from "@/context/branch-context";
import { columns, getColumnLabels } from "./columns";
import { useLocalStorageState } from "@/hooks/use-local-storage-state";
import { useLocalization } from "@/context/localization-context";


type UsersProps = {
  onCreateUser?: () => void;
  onEditUser?: (user: User) => void;
  onChangePassword?: (user: User) => void;
};

const USER_LOGIN_FILTER_STORAGE_KEY = "pizza-admin:user-login-filter:v1";
const USER_ROLE_FILTER_STORAGE_KEY = "pizza-admin:user-role-filter:v1";
const USER_BRANCH_FILTER_STORAGE_KEY = "pizza-admin:user-branch-filter:v1";

export default function Users({ onCreateUser, onEditUser, onChangePassword }: UsersProps) {
  const { t, tp, locale } = useLocalization();
  const { users, usersLoading } = useUserContext();
  const { getAvailableRoles } = useRole();
  const { branches } = useBranch();
  const availableRoles = getAvailableRoles();

  // Local state for filters (similar to branches)
  const [loginFilter, setLoginFilter] = useLocalStorageState(
    USER_LOGIN_FILTER_STORAGE_KEY,
    "",
  );
  const [roleFilter, setRoleFilter] = useLocalStorageState(
    USER_ROLE_FILTER_STORAGE_KEY,
    "",
  );
  const [branchFilter, setBranchFilter] = useLocalStorageState(
    USER_BRANCH_FILTER_STORAGE_KEY,
    "",
  );

  // A stored filter can outlive the role or branch it points to; an unknown
  // value would hide every user behind a blank select, so it counts as "all".
  const activeRoleFilter = availableRoles.some((role) => role._id === roleFilter)
    ? roleFilter
    : "";
  const activeBranchFilter = branches?.some(
    (branch) => branch.bb_branch_id && branch.bb_branch_id === branchFilter,
  )
    ? branchFilter
    : "";

  // Filter users based on filters (similar to branches)
  const filteredUsers = React.useMemo(() => {
    if (!users) return [];

    const search = loginFilter.trim().toLowerCase();
    
    return users.filter(user => {
      // Login / name filter
      if (
        search &&
        ![user.login, user.first_name, user.last_name].some((value) =>
          value?.toLowerCase().includes(search),
        )
      ) {
        return false;
      }
      
      // Role filter
      if (activeRoleFilter && user.role_id !== activeRoleFilter) {
        return false;
      }
      
      // Branch filter
      if (activeBranchFilter && user.branch_id !== activeBranchFilter) {
        return false;
      }
      
      return true;
    });
  }, [users, loginFilter, activeRoleFilter, activeBranchFilter]);

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({
    login: true,
    role_id: true,
    branch_id: true,
    enabled: true,
    updated_at: true,
    actions: true,
  });
  
  // Store pagination state to preserve it during refetch
  const [paginationState, setPaginationState] = React.useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // The page survives a refetch, but a changed filter starts from the first
  // page again, otherwise a shorter result leaves the table on an empty page.
  React.useEffect(() => {
    setPaginationState((current) =>
      current.pageIndex === 0 ? current : { ...current, pageIndex: 0 },
    );
  }, [loginFilter, activeRoleFilter, activeBranchFilter]);

  const handleEditUser = React.useCallback((user: User) => {
    onEditUser?.(user);
  }, [onEditUser]);

  const handleChangePassword = React.useCallback((user: User) => {
    onChangePassword?.(user);
  }, [onChangePassword]);

  // Memoize columns to prevent unnecessary re-renders (the locale keeps their texts current)
  const memoizedColumns = React.useMemo(
    () => columns(handleEditUser, handleChangePassword),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleEditUser, handleChangePassword, locale]
  );
  const columnLabels = React.useMemo(
    () => getColumnLabels(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale],
  );

  const table = useReactTable({
    data: filteredUsers,
    columns: memoizedColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPaginationState,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      pagination: paginationState,
    },
    initialState: {
      pagination: paginationState,
    },
    // Prevent automatic resets when data changes
    autoResetPageIndex: false,
    autoResetExpanded: false,
    // Stable row identity, so a row's controls keep their state across refetches
    getRowId: (row) => row._id,
  });

  return (
    <div className="w-full gap-4 flex flex-col">
      <div className="flex items-center gap-2">
        <Input
          placeholder={t("users.list.filterPlaceholder")}
          value={loginFilter}
          onChange={(event) => setLoginFilter(event.target.value)}
          className="max-w-sm"
        />
        <Select
          value={activeRoleFilter || "ALL"}
          onValueChange={(value) => setRoleFilter(value === "ALL" ? "" : value)}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder={t("users.list.roleFilter")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">{t("users.list.allRoles")}</SelectItem>
            {availableRoles.map((role) => (
              <SelectItem key={role._id} value={role._id}>
                {role.name || role.description}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={activeBranchFilter || "ALL"}
          onValueChange={(value) => setBranchFilter(value === "ALL" ? "" : value)}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder={t("users.list.branchFilter")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">{t("users.fields.allBranches")}</SelectItem>
            {branches?.filter(branch => branch.bb_branch_id).map((branch) => (
              <SelectItem key={branch.bb_branch_id} value={branch.bb_branch_id}>
                {branch.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="ml-auto flex items-center gap-2">
          <Button onClick={onCreateUser} className="gap-2">
            <Plus className="h-4 w-4" />
            {t("users.list.addUser")}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                {t("common.columns")} <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {columnLabels[column.id] ?? column.id}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {usersLoading ? (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="h-24 text-center"
                >
                  {t("common.loading")}
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="h-24 text-center"
                >
                  {t("users.list.empty")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between space-x-2 py-4">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">{t("common.perPage")}</p>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 20, 30, 50, 100].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center space-x-6 lg:space-x-8">
          <div className="flex min-w-[100px] items-center justify-center whitespace-nowrap px-2 text-sm font-medium">
            {t("common.page", {
              page: table.getState().pagination.pageIndex + 1,
              pages: table.getPageCount(),
            })}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">{t("common.firstPage")}</span>
              <ChevronsLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">{t("common.previousPage")}</span>
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">{t("common.nextPage")}</span>
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">{t("common.lastPage")}</span>
              <ChevronsRightIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="text-sm text-muted-foreground">
          {users && users.length !== filteredUsers.length
            ? tp("users.list.showing", users.length, { shown: filteredUsers.length })
            : tp("users.list.total", filteredUsers.length)}
        </div>
      </div>
    </div>
  );
}
